/**
 * Production error sink for React error boundaries.
 *
 * componentDidCatch is a black hole by default — errors caught by
 * GlobalErrorBoundary / ErrorBoundary used to be logged only when
 * `NODE_ENV === 'development'`, so production crashes vanished silently.
 * reportError() gives every boundary somewhere to report to, without
 * hard-coding a telemetry vendor (that's a product decision, not an
 * engineering one — see project notes on AI/architecture decisions
 * needing explicit approval).
 *
 * Every call does two things, unconditionally:
 *   1. console.error, tagged `[PrivacyGuard:<boundary>]`, so the error is
 *      never dropped even if no vendor is configured — visible in devtools
 *      and in any log-shipping console capture.
 *   2. Forwards to `window.__PRIVACYGUARD_ERROR_SINK__(error, info, meta)`
 *      if that global is defined — a pluggable hook a real telemetry
 *      vendor (Sentry, Datadog, a backend collector, ...) can assign at
 *      boot time without touching call sites or this module.
 *
 * Callers keep any of their own env-gated verbose logging (e.g. dev-only
 * componentStack dumps) — this helper does not replace that, it closes the
 * production gap on top of it.
 */

export function reportError(error, info, meta = {}) {
  const boundary = meta.boundary || 'ErrorBoundary';
  const componentStack = info && info.componentStack;

  // eslint-disable-next-line no-console
  console.error(`[PrivacyGuard:${boundary}]`, error, componentStack);

  const sink =
    typeof window !== 'undefined' ? window.__PRIVACYGUARD_ERROR_SINK__ : undefined;

  if (typeof sink === 'function') {
    try {
      sink(error, info, meta);
    } catch (sinkError) {
      // The configured sink itself threw — never let a bad sink crash the
      // boundary's own error handling, and never let the original error
      // disappear as a result.
      // eslint-disable-next-line no-console
      console.error('[PrivacyGuard:error-sink-failed]', sinkError);
    }
  }
}

export default reportError;
